import { type ComponentPropsWithoutRef, type ReactNode, type Ref, forwardRef } from 'react';

// =================================================================================================
// #194 (N4): gemeinsamer Rahmen aller Karten in „Meine Tests" (Testkarte, Fehler prüfen).
//
// Kundenwunsch 14.10., korrigiert 15.10.: jede Karte bekommt eine Kopfzeile und einen klaren Rahmen
// auf weißem Grund. Orange (zu viel Rot) gehört NUR der Karte „Fehler prüfen"; Testkarten — auch
// Retest — sind `neutral`, bei Retest steht nur das Zustandswort orange (`header.rightAccent`).
// Entscheidung: NUR `retest` (Karte „Fehler prüfen") ist gefärbt — orange Kopfzeile, oranger Rahmen
// (`status-fail`, im hellen Modus #ea5b0b, im dunklen #ff7a38). Alles andere ist `neutral`: graue
// Kopfzeile (`bg-subtle`), grauer Rahmen (`--border`). Kartengrund ist `bg-surface` (hell weiß,
// dunkel die Flächenfarbe wie bisher).
//
// Auswahl: NICHT über den Rahmen — Orange heißt „Handlungsbedarf", ein oranger Auswahlrahmen wäre davon
// nicht zu unterscheiden. Die Auswahl ist ein 2px-Ring in `text-primary` (hell Marineblau #1b365d,
// dunkel fast Weiß #e6ebf4): in beiden Themen klar abgesetzt vom Orange und vom Kartengrund. Der
// Ring sitzt mit 1px Abstand zur Seitenfarbe (`bg-page`) außerhalb des Rahmens, damit eine gewählte
// Retest-Karte ihren orangen Rahmen behält. (`brand-primary` taugt dafür nicht: es ist in beiden
// Themen Marineblau und verschwindet auf dem dunklen Grund.) Der Hover nutzt denselben Ring dünn.
// =================================================================================================

export type CardTone = 'retest' | 'neutral';

const TONE_FRAME: Record<CardTone, string> = {
  retest: 'border-status-fail',
  neutral: 'border-[var(--border)]',
};

const TONE_HEADER: Record<CardTone, string> = {
  // Text auf der orangen Tönung: `--sev-high-fg` (hell #7c2d12, dunkel #fdba74) statt `status-fail`
  // selbst — Orange auf 10 %-Orange-Tönung kommt hell nur auf rund 3,5:1, die Severity-Paare sind für
  // ≥ 4,5:1 ausgelegt (styles/tokens/light.css, dark.css). Rahmen und Tönung bleiben `status-fail`.
  retest: 'bg-status-fail/10 text-[var(--sev-high-fg)] border-status-fail/40',
  neutral: 'bg-bg-subtle text-text-primary border-[var(--border)]',
};

export interface CardHeader {
  /** Links, bei Testkarten die Testfall-ID, bei „Fehler prüfen" die Defect-ID (Mono). */
  left: ReactNode;
  /** Rechts, das Zustands-Label (Retest, Fehler prüfen …); fehlt bei einer offenen Karte. */
  right?: ReactNode;
  /**
   * #194 (15.10.): `right` in Orange (`text-status-fail`) — für das Zustandswort „Retest" auf der
   * NEUTRALEN Kopfzeile einer Testkarte. Der Rahmen bleibt grau; nur das Wort trägt die Farbe.
   */
  rightAccent?: boolean;
  /** `id` des Spans mit `left` — der Aufrufer zeigt per `aria-labelledby` darauf (Minor 1). */
  leftId?: string;
  /** Vor `left`, z. B. das Weitergeben-Kästchen (#195). */
  leading?: ReactNode;
}

type FrameProps = Omit<ComponentPropsWithoutRef<'div'>, 'className' | 'children'> & {
  tone: CardTone;
  selected: boolean;
  header: CardHeader;
  /** `button`: die ganze Karte ist EIN Knopf (Fehler prüfen); sonst ein Container (Testkarte). */
  as?: 'div' | 'button';
  /** Hover-Ring; bei gesperrten Karten aus, damit sie nicht anklickbar wirken. */
  hoverable?: boolean;
  /** Zusatzklassen des Aufrufers, z. B. die Dämpfung gesperrter Karten. */
  className?: string;
  children: ReactNode;
};

export const CardFrame = forwardRef<HTMLElement, FrameProps>(function CardFrame(
  { tone, selected, header, as = 'div', hoverable = true, className = '', children, ...rest },
  ref,
) {
  const classes = [
    'relative flex w-full flex-col overflow-hidden rounded-card border bg-bg-surface text-left',
    TONE_FRAME[tone],
    selected
      ? 'ring-2 ring-text-primary ring-offset-1 ring-offset-bg-page'
      : hoverable
        ? 'hover:ring-1 hover:ring-text-muted'
        : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  // #194 Final-Review: Markierung der gewählten Karte — die Seite setzt nach dem Bestätigen einer
  // Fehlerprüfung den Fokus darauf (my-tests-page.tsx), ohne die Kartenarten zu kennen.
  const selectedMark = selected ? { 'data-card-selected': '' } : {};
  const content = (
    <>
      {/* <span>: bei `as="button"` darf im Knopf nur Fließtext-Inhalt stehen (kein <div>). */}
      <span
        data-card-header
        className={`flex w-full items-center gap-2 border-b px-3 py-1.5 text-xs ${TONE_HEADER[tone]}`}
      >
        {header.leading}
        <span id={header.leftId} className="min-w-0 break-words font-mono font-medium">
          {header.left}
        </span>
        {header.right && (
          <span
            className={`ml-auto shrink-0 text-[11px] font-bold uppercase tracking-wide${
              header.rightAccent ? ' text-status-fail' : ''
            }`}
          >
            {header.right}
          </span>
        )}
      </span>
      {children}
    </>
  );
  if (as === 'button') {
    return (
      <button
        ref={ref as Ref<HTMLButtonElement>}
        type="button"
        className={classes}
        {...selectedMark}
        {...(rest as ComponentPropsWithoutRef<'button'>)}
      >
        {content}
      </button>
    );
  }
  return (
    <div ref={ref as Ref<HTMLDivElement>} className={classes} {...selectedMark} {...rest}>
      {content}
    </div>
  );
});
